import React, { useCallback, useRef, useState } from "react";
import { UploadCloud, X, FileText } from "lucide-react";

/**
 * FileDropzone — drag-and-drop multi-file input, dark luxury styling.
 * Props:
 *   files: File[]  (controlled)
 *   onChange: (nextFiles: File[]) => void
 *   accept: string[]  extensions (with dot), e.g. [".pdf", ".dwg"]
 *   maxFiles: number  (default 5)
 *   maxTotalBytes: number (default 25 * 1024 * 1024)
 *   disabled: bool
 */
const ACCEPT_DEFAULT = [
    ".pdf",
    ".png",
    ".jpg",
    ".jpeg",
    ".dwg",
    ".dxf",
    ".rvt",
    ".zip",
];

const fmtSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const FileDropzone = ({
    files = [],
    onChange,
    accept = ACCEPT_DEFAULT,
    maxFiles = 5,
    maxTotalBytes = 25 * 1024 * 1024,
    disabled = false,
    "data-testid": testId = "file-dropzone",
}) => {
    const [dragActive, setDragActive] = useState(false);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    const acceptStr = accept.join(",");

    const validateAndAdd = useCallback(
        (incoming) => {
            setError(null);
            const list = Array.from(incoming || []);
            const accepted = [];
            for (const f of list) {
                const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
                if (!accept.includes(ext)) {
                    setError(
                        `Unsupported file type: ${f.name}. Accepted: ${accept.join(", ")}`,
                    );
                    return;
                }
                accepted.push(f);
            }
            const next = [...files, ...accepted];
            if (next.length > maxFiles) {
                setError(`Maximum ${maxFiles} files.`);
                return;
            }
            const total = next.reduce((n, f) => n + (f.size || 0), 0);
            if (total > maxTotalBytes) {
                setError(
                    `Total upload exceeds ${(maxTotalBytes / (1024 * 1024)).toFixed(0)} MB.`,
                );
                return;
            }
            onChange(next);
        },
        [files, accept, maxFiles, maxTotalBytes, onChange],
    );

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (disabled) return;
        validateAndAdd(e.dataTransfer.files);
    };

    const removeAt = (idx) => {
        const next = files.filter((_, i) => i !== idx);
        onChange(next);
        setError(null);
    };

    return (
        <div data-testid={testId}>
            <label className="block text-[12px] font-medium uppercase tracking-widest-2 text-text-secondary">
                Attachments <span className="text-text-muted normal-case tracking-normal">(optional)</span>
            </label>
            <div
                className={`mt-2 relative rounded-xl border border-dashed transition-all duration-200 ease-luxury ${
                    dragActive
                        ? "border-[rgba(124,255,79,0.55)] bg-[rgba(124,255,79,0.04)]"
                        : "border-border-default bg-[var(--bg-elevated-2)] hover:border-[rgba(255,255,255,0.18)]"
                } ${disabled ? "opacity-60 pointer-events-none" : ""}`}
                onDragEnter={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(true);
                }}
                onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setDragActive(false);
                }}
                onDrop={onDrop}
            >
                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    accept={acceptStr}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                    onChange={(e) => validateAndAdd(e.target.files)}
                    data-testid={`${testId}-input`}
                    aria-label="Upload attachments"
                />
                <div className="pointer-events-none flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border-subtle bg-[var(--bg-elevated)]">
                        <UploadCloud
                            className="h-5 w-5 text-[var(--accent)]"
                            strokeWidth={1.5}
                        />
                    </span>
                    <p className="text-[15px] text-text-primary">
                        Drag and drop files, or{" "}
                        <span className="text-[var(--accent)]">click to browse</span>
                    </p>
                    <p className="text-[12px] text-text-muted">
                        {accept.join(" · ")} — up to {maxFiles} files,{" "}
                        {(maxTotalBytes / (1024 * 1024)).toFixed(0)} MB total
                    </p>
                </div>
            </div>

            {files.length > 0 && (
                <ul
                    className="mt-4 space-y-2"
                    data-testid={`${testId}-list`}
                >
                    {files.map((f, i) => (
                        <li
                            key={`${f.name}-${i}`}
                            className="flex items-center gap-3 rounded-lg border border-border-subtle bg-[var(--bg-elevated)] px-4 py-3"
                            data-testid={`${testId}-file-${i}`}
                        >
                            <FileText
                                className="h-4 w-4 shrink-0 text-text-muted"
                                strokeWidth={1.5}
                            />
                            <div className="min-w-0 flex-1">
                                <p className="truncate text-[14px] text-text-primary">
                                    {f.name}
                                </p>
                                <p className="text-[12px] text-text-muted">
                                    {fmtSize(f.size)}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => removeAt(i)}
                                aria-label={`Remove ${f.name}`}
                                data-testid={`${testId}-remove-${i}`}
                                className="ml-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-text-muted transition-colors duration-200 hover:text-text-primary hover:bg-[var(--bg-elevated-2)]"
                            >
                                <X className="h-4 w-4" strokeWidth={1.5} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {error && (
                <p
                    className="mt-3 text-[12.5px] text-red-400/90"
                    data-testid={`${testId}-error`}
                    role="alert"
                >
                    {error}
                </p>
            )}
        </div>
    );
};

export default FileDropzone;
